/**
 * Agent image service — hand-curated Pexels photo URLs per topic.
 * Pexels images are free to use with no API key required.
 * Each category has a primary + backup photo so onError can retry.
 */

const px = (id) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop`;

// Each entry: { match: [keywords], photos: [primary, backup] }
const CATEGORY_PHOTOS = [
  // --- Finance & Banking ---
  {
    match: ['finance', 'financial', 'billing', 'invoice', 'accounting', 'budget', 'cost'],
    photos: [px(534216), px(187041)],
  },
  {
    match: ['invest', 'stock', 'trading', 'portfolio', 'fund', 'market', 'equity', 'asset'],
    photos: [px(7567526), px(6801648)],
  },
  {
    match: ['payment', 'transaction', 'transfer', 'wire', 'settlement', 'clearing'],
    photos: [px(4968430), px(534216)],
  },
  {
    match: ['credit', 'loan', 'lending', 'mortgage', 'scoring', 'underwriting', 'origination'],
    photos: [px(187041), px(534216)],
  },
  {
    match: ['fraud', 'detection', 'aml', 'anti-money', 'suspicious', 'anomaly'],
    photos: [px(60504), px(5380664)],
  },
  {
    match: ['risk', 'exposure', 'stress test', 'var', 'hedg'],
    photos: [px(590022), px(186461)],
  },
  {
    match: ['kyc', 'know your customer', 'onboard', 'identity', 'verification', 'due diligence'],
    photos: [px(3184328), px(3182826)],
  },
  {
    match: ['regulatory', 'regulation', 'compliance', 'reporting', 'basel', 'ifrs', 'gaap', 'audit'],
    photos: [px(5668858), px(4427777)],
  },
  {
    match: ['esg', 'sustainability', 'climate', 'green', 'environmental'],
    photos: [px(1108572), px(7567526)],
  },
  {
    match: ['insurance', 'claim', 'policy', 'premium', 'actuar'],
    photos: [px(4427777), px(5668858)],
  },
  {
    match: ['wealth', 'private banking', 'advisory', 'client advisor', 'relationship manager'],
    photos: [px(3182826), px(3184328)],
  },
  {
    match: ['wallet', 'crypto', 'blockchain', 'defi', 'token', 'digital asset'],
    photos: [px(8370752), px(7567526)],
  },
  // --- Data & Analytics ---
  {
    match: ['sql', 'database', 'db', 'query', 'postgres', 'mysql', 'data warehouse'],
    photos: [px(1148820), px(325229)],
  },
  {
    match: ['chart', 'graph', 'dashboard', 'visualization', 'plot'],
    photos: [px(590022), px(669615)],
  },
  {
    match: ['analytics', 'metric', 'kpi', 'trend', 'insight'],
    photos: [px(186461), px(7947541)],
  },
  {
    match: ['spreadsheet', 'excel', 'csv', 'table'],
    photos: [px(1181244), px(669610)],
  },
  // --- AI & Technology ---
  {
    match: ['artificial intelligence', 'neural', 'deep learning', 'llm', 'gpt', 'generative'],
    photos: [px(8386440), px(3861969)],
  },
  {
    match: [' ai ', 'machine learning', ' ml '],
    photos: [px(3861969), px(8386440)],
  },
  {
    match: ['chatbot', 'chat', 'conversation', 'nlp', 'language model'],
    photos: [px(4226122), px(8294643)],
  },
  {
    match: ['bot', 'robot', 'assistant', 'agent', 'automation', 'workflow', 'pipeline'],
    photos: [px(3862130), px(2599244)],
  },
  {
    match: ['code', 'api', 'script', 'sdk', 'developer', 'programming', 'software'],
    photos: [px(1181671), px(574073)],
  },
  {
    match: ['security', 'auth', 'permission', 'encrypt', 'cyber', 'access control'],
    photos: [px(60504), px(5380664)],
  },
  {
    match: ['cloud', 'aws', 'azure', 'gcp', 'storage', 'infrastructure', 'server'],
    photos: [px(1148820), px(1181675)],
  },
  // --- Documents & Knowledge ---
  {
    match: ['document', 'pdf', 'file', 'report', 'contract', 'letter'],
    photos: [px(95916), px(1181403)],
  },
  {
    match: ['knowledge', 'rag', 'retrieval', 'wiki', 'library'],
    photos: [px(159711), px(1290141)],
  },
  {
    match: ['summary', 'summarize', 'extract', 'classify'],
    photos: [px(267669), px(1181403)],
  },
  // --- People & Operations ---
  {
    match: ['customer', 'user', 'client', 'profile', 'crm', 'service desk', 'support'],
    photos: [px(3184328), px(3182826)],
  },
  {
    match: ['hr', 'human resources', 'recruit', 'employee', 'talent', 'workforce'],
    photos: [px(3184423), px(1181562)],
  },
  {
    match: ['email', 'mail', 'inbox', 'notification', 'alert'],
    photos: [px(1591060), px(7148964)],
  },
  {
    match: ['legal', 'contract', 'law', 'litigation'],
    photos: [px(5668858), px(4427777)],
  },
];

// All unique verified photos from CATEGORY_PHOTOS above, deduplicated.
// Large enough that the pool never exhausts across a full agent list.
const OVERFLOW_POOL = [
  // Finance & banking
  px(534216), px(187041), px(7567526), px(6801648), px(8370752),
  px(5668858), px(4427777),
  // Analytics & dashboards
  px(590022), px(669615), px(186461), px(7947541), px(669610),
  px(1181244),
  // AI & tech
  px(8386440), px(3861969), px(4226122), px(8294643), px(2599244),
  px(8566526),
  // People & teams
  px(3184328), px(3182826), px(3184423), px(1181562),
  // Documents & knowledge
  px(95916), px(1181403), px(159711), px(1290141), px(267669),
  // Code & infrastructure
  px(1181671), px(574073), px(1148820), px(325229), px(1181675),
  px(2881229),
  // Security
  px(60504), px(5380664),
  // Automation
  px(3862130),
  // Communications
  px(1591060), px(7148964),
  // Search & discovery
  px(1181772), px(1438081),
  // Other verified
  px(207665), px(1181403), px(3769138), px(592753), px(905163),
  px(6177645),
];

const matchCategory = (text = '') => {
  const lower = ` ${text.toLowerCase()} `;
  for (const cat of CATEGORY_PHOTOS) {
    if (cat.match.some(k => lower.includes(k))) return cat.photos;
  }
  return [];
};

const primaryCache = new Map();
const backupCache  = new Map();
const usedPhotos   = new Set();

const pickUnused = (candidates) => {
  for (const photo of candidates) {
    if (!usedPhotos.has(photo)) return photo;
  }
  return null;
};

const ALL_PHOTOS = [
  ...OVERFLOW_POOL,
  ...CATEGORY_PHOTOS.flatMap(c => c.photos),
];

const assignPhoto = (cacheKey, text) => {
  if (!primaryCache.has(cacheKey)) {
    const categoryPhotos = matchCategory(text);
    // Try category match first, then overflow, then full pool — never reuse.
    const primary =
      pickUnused(categoryPhotos) ??
      pickUnused(OVERFLOW_POOL) ??
      pickUnused(ALL_PHOTOS) ??
      OVERFLOW_POOL[0]; // absolute last resort (pool truly exhausted)
    usedPhotos.add(primary);
    primaryCache.set(cacheKey, primary);

    const backup =
      pickUnused([...categoryPhotos, ...OVERFLOW_POOL, ...ALL_PHOTOS]) ??
      OVERFLOW_POOL[1] ??
      OVERFLOW_POOL[0];
    backupCache.set(cacheKey, backup);
  }
};

export const getAgentImageUrl = (name = '') => {
  assignPhoto(name, name);
  return primaryCache.get(name);
};

export const getAgentImageFallbackUrl = (name = '') => {
  assignPhoto(name, name);
  return backupCache.get(name) ?? OVERFLOW_POOL[0];
};

export const getUseCaseImageUrl = (useCase = {}) => {
  const key = `uc_${useCase.id ?? useCase.name}`;
  const text = `${useCase.name ?? ''} ${useCase.description ?? ''}`;
  assignPhoto(key, text);
  return primaryCache.get(key);
};

export const getUseCaseImageFallbackUrl = (useCase = {}) => {
  const key = `uc_${useCase.id ?? useCase.name}`;
  const text = `${useCase.name ?? ''} ${useCase.description ?? ''}`;
  assignPhoto(key, text);
  return backupCache.get(key) ?? OVERFLOW_POOL[0];
};

export const resetImageAssignments = () => {
  primaryCache.clear();
  backupCache.clear();
  usedPhotos.clear();
};
